import type { ChatTransport, UIMessage, UIMessageChunk, ChatRequestOptions } from 'ai';
import type { ChatService } from '../types';
import { RealChatService } from '../real/RealChatService';
import { nanoid } from 'nanoid';

/**
 * Configuration for RealChatTransport
 */
export interface RealChatTransportConfig {
  /** Base URL for the mamba-server API */
  apiBaseUrl: string;
  /** Enable debug logging */
  debug?: boolean;
}

/**
 * AI SDK ChatTransport implementation for the real mamba-server backend.
 * Converts AI SDK messages to backend format and SSE responses to UIMessageChunks.
 */
export class RealChatTransport implements ChatTransport<UIMessage> {
  private chatService: ChatService;
  private debug: boolean;

  constructor(config: RealChatTransportConfig) {
    if (!config.apiBaseUrl) {
      throw new Error('RealChatTransport: apiBaseUrl is required');
    }

    this.chatService = new RealChatService({
      apiBaseUrl: config.apiBaseUrl,
      debug: config.debug,
    });
    this.debug = config.debug ?? false;
  }

  /**
   * Send messages and receive a streaming response.
   * Called by the AI SDK's useChat hook.
   */
  sendMessages = async (
    options: {
      trigger: 'submit-message' | 'regenerate-message';
      chatId: string;
      messageId: string | undefined;
      messages: UIMessage[];
      abortSignal: AbortSignal | undefined;
    } & ChatRequestOptions
  ): Promise<ReadableStream<UIMessageChunk>> => {
    const { messages, abortSignal, body } = options;

    if (this.debug) {
      console.log('[RealChatTransport] sendMessages called:', {
        messageCount: messages.length,
        trigger: options.trigger,
      });
    }

    // Extract model from request body if provided
    const model = (body as { model?: string })?.model ?? 'openai/gpt-4o';

    // Get the streaming response from the real service
    const rawStream = await this.chatService.streamChat({
      messages,
      model,
      abortSignal,
    });

    // Parse SSE events and convert to UIMessageChunk stream
    return this.parseSSEStream(rawStream, abortSignal);
  };

  /**
   * Reconnect to an existing stream.
   *
   * Stream reconnection is not supported by the mamba-server backend.
   * This method always returns null, which signals to the AI SDK that
   * reconnection should not be attempted.
   *
   * @returns null - reconnection not supported
   */
  reconnectToStream = async (
    _options: { chatId: string } & ChatRequestOptions
  ): Promise<ReadableStream<UIMessageChunk> | null> => {
    // Stream reconnection is not supported - returns null per ChatTransport interface
    return null;
  };

  /**
   * Parse SSE stream into UIMessageChunk stream
   */
  private parseSSEStream(
    rawStream: ReadableStream<Uint8Array>,
    abortSignal?: AbortSignal
  ): ReadableStream<UIMessageChunk> {
    const reader = rawStream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentTextId = '';
    let textStarted = false;
    const debug = this.debug;

    return new ReadableStream<UIMessageChunk>({
      async pull(controller) {
        if (abortSignal?.aborted) {
          reader.cancel();
          controller.close();
          return;
        }

        try {
          const { value, done } = await reader.read();

          if (done) {
            // Send text-end if we had text content
            if (textStarted && currentTextId) {
              controller.enqueue({
                type: 'text-end',
                id: currentTextId,
              });
            }
            controller.close();
            return;
          }

          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE events
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || ''; // Keep incomplete data in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const jsonStr = line.slice(6);
              try {
                const event = JSON.parse(jsonStr);
                const chunks = convertToUIMessageChunks(
                  event,
                  currentTextId,
                  textStarted,
                  (id) => { currentTextId = id; },
                  (started) => { textStarted = started; },
                  debug
                );
                for (const chunk of chunks) {
                  controller.enqueue(chunk);
                }
              } catch (e) {
                if (debug) {
                  console.error('[RealChatTransport] Failed to parse SSE event:', e);
                }
              }
            }
          }
        } catch (error) {
          if (abortSignal?.aborted) {
            controller.close();
          } else {
            controller.error(error);
          }
        }
      },

      cancel() {
        reader.cancel();
      },
    });
  }
}

/**
 * Convert backend stream events to UIMessageChunk format
 */
function convertToUIMessageChunks(
  event: {
    type: string;
    textDelta?: string;
    toolCallId?: string;
    toolName?: string;
    args?: Record<string, unknown>;
    result?: Record<string, unknown>;
    error?: string;
  },
  currentTextId: string,
  textStarted: boolean,
  setTextId: (id: string) => void,
  setTextStarted: (started: boolean) => void,
  debug?: boolean
): UIMessageChunk[] {
  const chunks: UIMessageChunk[] = [];

  switch (event.type) {
    case 'text-delta': {
      let chunkId: string;
      if (!textStarted) {
        chunkId = nanoid();
        setTextId(chunkId);
        setTextStarted(true);
        chunks.push({
          type: 'text-start',
          id: chunkId,
        });
      } else {
        chunkId = currentTextId;
      }
      chunks.push({
        type: 'text-delta',
        id: chunkId || nanoid(),
        delta: event.textDelta || '',
      });
      break;
    }

    case 'tool-call':
      // End any pending text
      if (textStarted && currentTextId) {
        chunks.push({
          type: 'text-end',
          id: currentTextId,
        });
        setTextStarted(false);
      }
      chunks.push({
        type: 'tool-input-available',
        toolCallId: event.toolCallId || nanoid(),
        toolName: event.toolName || '',
        input: event.args || {},
      });
      break;

    case 'tool-result':
      chunks.push({
        type: 'tool-output-available',
        toolCallId: event.toolCallId || '',
        output: event.result || {},
      });
      break;

    case 'finish':
      // End any pending text
      if (textStarted && currentTextId) {
        chunks.push({
          type: 'text-end',
          id: currentTextId,
        });
        setTextStarted(false);
      }
      chunks.push({
        type: 'finish',
        finishReason: 'stop',
      });
      break;

    case 'error':
      if (debug) {
        console.error('[RealChatTransport] Stream error event:', event.error);
      }
      chunks.push({
        type: 'error',
        errorText: event.error || 'An error occurred',
      });
      break;
  }

  return chunks;
}

/**
 * Create a RealChatTransport instance
 */
export function createRealChatTransport(
  config: RealChatTransportConfig
): ChatTransport<UIMessage> {
  return new RealChatTransport(config);
}
